/**
 * SafeIntersections crash-risk calculator — pure functions, no UI.
 *
 * Ported verbatim from the React source
 * (`safeintersections/src/components/SafeIntersectionsCalculator/safeIntersectionsLib.ts`).
 * The model constants and factor thresholds come from research prepared by
 * Shane Turner; do not adjust them without a methodology change.
 */

const A = 0.0159874015879525;
const B = 0.83968328031782;

// Band index (0 = at or below `lo`, 2 = above `hi`) shared by the model factors and the calculator sliders.
const band = (v: number, lo: number, hi: number) => (v <= lo ? 0 : v > hi ? 2 : 1);
export const qMajorBand = (qMajor: number) => band(qMajor, 1550, 4250);
export const skewAngleBand = (minorSkewAngle: number) => band(minorSkewAngle, 140, 165);
export const approachDistBand = (minMinorApproachDistMeters: number) =>
	band(minMinorApproachDistMeters, 500, 1500);

const getFactorQMajor = (qMajor: number) =>
	[-0.311370268387286, 0, -0.291153946577277][qMajorBand(qMajor)];
const getFactorSkewAngle = (minorSkewAngle: number) =>
	[-0.278567092074852, 0, 0.14860794690972][skewAngleBand(minorSkewAngle)];
const getFactorMinorApproachDistMeters = (minMinorApproachDistMeters: number) =>
	[-0.315800679952174, 0, 0.111236199195077][approachDistBand(minMinorApproachDistMeters)];

export const calculateCrashRisk = (
	qMinor: number,
	qMajor: number,
	minorSkewAngle: number,
	minMinorApproachDistMeters: number
) => {
	if (qMinor === 0) return;
	return (
		A *
		Math.pow(qMinor, B) *
		(1 +
			getFactorQMajor(qMajor) +
			getFactorSkewAngle(minorSkewAngle) +
			getFactorMinorApproachDistMeters(minMinorApproachDistMeters))
	);
};

export const predictedCrashes10yr = (crask_risk_nz_20yr: number | undefined) => {
	if (crask_risk_nz_20yr === undefined) return;
	const yearsCorrection = 0.5; // NZ uses 20 years, USA uses 10
	const usaScaling = 1.9547; // USA scale-up factor
	return crask_risk_nz_20yr * yearsCorrection * usaScaling;
};

// Countermeasures
export enum ECountermeasures {
	SignageDelineationUpgrade = 'Intersection signage/delineation upgrade',
	AdvanceWarningSignage = 'Advance warning signage (plus good delineation)',
	SpeedLimitReduction = 'Permanent speed limit reduction',
	ActivatedWarningSigns = 'Activated warning signs (no speed limit reduction)',
	ActivatedWarningSignsWithSpeedLimitReduction = 'Activated warning signs (plus speed limit reduction)',
	Lighting = 'Add intersection street lighting',
	InstallRoundabout = 'Install roundabout',
	StaggerJunctions = 'Staggered junctions in place of Crossroads',
	CloseIntersection = 'Close intersection',
	GradeSeparation = 'Grade separation'
}

export const crashModificationFactors = {
	// Values derived from research prepared by Shane Turner
	[ECountermeasures.SignageDelineationUpgrade]: 0.15,
	[ECountermeasures.AdvanceWarningSignage]: 0.07,
	[ECountermeasures.SpeedLimitReduction]: 0.3,
	[ECountermeasures.ActivatedWarningSigns]: 0.25,
	[ECountermeasures.ActivatedWarningSignsWithSpeedLimitReduction]: 0.35,
	[ECountermeasures.Lighting]: 0.3,
	[ECountermeasures.InstallRoundabout]: 0.75,
	[ECountermeasures.StaggerJunctions]: 0.35,
	/*
	 * FIXME: custom values from Steve
	 * These have been adjusted as the values from research for these countermeasures were
	 * targeting much more major intersections than our rural crossroads
	 */
	[ECountermeasures.GradeSeparation]: 0.95,
	[ECountermeasures.CloseIntersection]: 1
};

export enum EIntersectionClass {
	Class1 = 'Class 1',
	Class2 = 'Class 2',
	Class3 = 'Class 3'
}

export const getIntersectionClass = (
	crashRisk: number | undefined,
	lowerBand: number | undefined,
	upperBand: number | undefined
): EIntersectionClass | undefined => {
	if (crashRisk === undefined || lowerBand === undefined || upperBand === undefined) return;
	if (crashRisk < lowerBand) {
		return EIntersectionClass.Class3;
	} else if (crashRisk < upperBand) {
		return EIntersectionClass.Class2;
	}
	return EIntersectionClass.Class1;
};
